import React, { useState, useEffect } from "react";
import API from "../services/api";
import { printNote } from "./PrintPDFUtils";
import { todayEastern } from "../dateUtils";

/**
 * IntakeAssessmentModal - Form for creating new client intake assessment notes
 * Based on the PDF template provided
 */
function IntakeAssessmentModal({ isOpen, onClose, onSubmit, clientId, clientName }) {
  if (!isOpen) return null;

  const [date, setDate] = useState(todayEastern());
  const [formData, setFormData] = useState({
    // Basic form data
    presentingProblems: "",
    familyMedicalHistory: "",
    
    // Trauma History
    traumaHistory: "No trauma history reported", // Default value
    traumaHistoryOther: "",
    
    // Substance Abuse
    substanceAbuseFamilyHistory: ["no family history reported"], // Allow multiple selections
    substanceAbuseFamilyHistoryOther: "",
    substanceAbuseStatus: "no history of substance abuse", // Default value
    
    // Previous Therapy 
    previousTherapyExperiences: "No previous therapy experiences", // Default value
    previousTherapyNotes: "",
    
    // Self-harm history
    selfHarmHistory: "",
    
    // Relationship
    relationshipStatus: "single, never married", // Default value
    relationshipSatisfaction: "satisfied", // Default value
    
    // Support & Strengths
    protectiveFactors: "",
    copingSkills: "",
    
    // Mental Status
    mentalStatusExam: ""
  });

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Handle checkbox-type multiple selections (for family substance abuse history)
  const handleMultiSelectChange = (field, value) => {
    let updatedValues;
    
    if (formData[field].includes(value)) {
      // Remove value if it's already selected
      updatedValues = formData[field].filter(item => item !== value);
    } else {
      // Add value if it's not already selected
      updatedValues = [...formData[field], value];
      
      // If "no family history reported" is selected, remove it when other options are chosen
      if (value !== "no family history reported" && updatedValues.includes("no family history reported")) {
        updatedValues = updatedValues.filter(item => item !== "no family history reported");
      }
      
      // If "no family history reported" is selected, clear all other selections
      if (value === "no family history reported") {
        updatedValues = ["no family history reported"];
      }
    }
    
    setFormData({
      ...formData,
      [field]: updatedValues
    });
  };

  // Save the assessment
  const handleSave = () => {
    const assessmentData = {
      date,
      clientId,
      type: "IntakeAssessment",
      ...formData
    };
    
    onSubmit(assessmentData);
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>New Intake Assessment</h2>
        <div style={styles.clientInfo}>
          <strong>Client:</strong> {clientName} | <strong>Date:</strong> {date}
          <button 
            onClick={() => setDate(new Date().toISOString().split("T")[0])}
            style={styles.smallButton}
          >
            Set Today
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>

        <div style={styles.formScrollArea}>
          {/* Presenting Problems */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Presenting Problem(s), including current symptoms:</h3>
            <textarea
              rows="4"
              value={formData.presentingProblems}
              onChange={(e) => handleChange("presentingProblems", e.target.value)}
              style={styles.textArea}
              placeholder="Describe client's presenting problems and symptoms..."
            />
          </div>

          {/* Family/Medical History */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>History: (family/medical)</h3>
            <textarea
              rows="4"
              value={formData.familyMedicalHistory}
              onChange={(e) => handleChange("familyMedicalHistory", e.target.value)}
              style={styles.textArea}
              placeholder="Describe relevant family and medical history..."
            />
          </div>

          {/* Trauma History */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Trauma History</h3>
            <div style={styles.optionsContainer}>
              <select
                value={formData.traumaHistory}
                onChange={(e) => handleChange("traumaHistory", e.target.value)}
                style={styles.select}
              >
                <option value="No trauma history reported">No trauma history reported</option>
                <option value="witnessed physical/verbal/sexual abuse towards others">Witnessed physical/verbal/sexual abuse towards others</option>
                <option value="experienced physical/verbal/sexual abuse from others">Experienced physical/verbal/sexual abuse from others</option>
                <option value="other">Other</option>
              </select>
              
              {formData.traumaHistory === "other" && (
                <input
                  type="text"
                  value={formData.traumaHistoryOther}
                  onChange={(e) => handleChange("traumaHistoryOther", e.target.value)}
                  style={styles.input}
                  placeholder="Specify other trauma history..."
                />
              )}
            </div>
          </div>

          {/* Substance Abuse History */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Substance Abuse History</h3>
            <h4 style={styles.subSectionHeader}>Family History:</h4>
            <div style={styles.checkboxContainer}>
              {[
                { id: "noHistory", label: "no family history reported" },
                { id: "father", label: "father" },
                { id: "mother", label: "mother" },
                { id: "grandparents", label: "grandparents" },
                { id: "siblings", label: "siblings" },
                { id: "spouse", label: "spouse/partner" },
                { id: "other", label: "other" }
              ].map(option => (
                <div key={option.id} style={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id={`family-${option.id}`}
                    checked={formData.substanceAbuseFamilyHistory.includes(option.label)}
                    onChange={() => handleMultiSelectChange("substanceAbuseFamilyHistory", option.label)}
                  />
                  <label htmlFor={`family-${option.id}`}>{option.label}</label>
                </div>
              ))}
            </div>
            
            {formData.substanceAbuseFamilyHistory.includes("other") && (
              <input
                type="text"
                value={formData.substanceAbuseFamilyHistoryOther}
                onChange={(e) => handleChange("substanceAbuseFamilyHistoryOther", e.target.value)}
                style={{...styles.input, marginTop: "0.5rem"}}
                placeholder="Specify other family members..."
              />
            )}

            <h4 style={styles.subSectionHeader}>Substance Abuse Status for Client:</h4>
            <select
              value={formData.substanceAbuseStatus}
              onChange={(e) => handleChange("substanceAbuseStatus", e.target.value)}
              style={styles.select}
            >
              <option value="no history of substance abuse">no history of substance abuse</option>
              <option value="active abuse">active abuse</option>
              <option value="early full remission">early full remission</option>
              <option value="early partial remission">early partial remission</option>
              <option value="sustained full remission">sustained full remission</option>
              <option value="sustained partial remission">sustained partial remission</option>
            </select>
          </div>

          {/* Previous Therapy Experiences */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Previous Therapy Experiences:</h3>
            <select
              value={formData.previousTherapyExperiences}
              onChange={(e) => handleChange("previousTherapyExperiences", e.target.value)}
              style={styles.select}
            >
              <option value="No previous therapy experiences">No previous therapy experiences</option>
              <option value="Prior outpatient psychotherapy">Prior outpatient psychotherapy</option>
              <option value="prior inpatient psychotherapy">Prior inpatient psychotherapy</option>
              <option value="multiple therapy experiences">Multiple therapy experiences</option>
            </select>
            
            <textarea
              rows="3"
              value={formData.previousTherapyNotes}
              onChange={(e) => handleChange("previousTherapyNotes", e.target.value)}
              style={{...styles.textArea, marginTop: "0.5rem"}}
              placeholder="Notes about previous therapy experiences..."
            />
          </div>

          {/* SI/HI/Self-harm */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>History/Current SI/HI/self-harm:</h3>
            <textarea
              rows="3"
              value={formData.selfHarmHistory}
              onChange={(e) => handleChange("selfHarmHistory", e.target.value)}
              style={styles.textArea}
              placeholder="Document any history or current suicidal ideation, homicidal ideation, or self-harm behaviors..."
            />
          </div>

          {/* Current relationship status */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Current relationship status/satisfaction:</h3>
            <div style={styles.twoColumnSection}>
              <div style={styles.column}>
                <h4 style={styles.subSectionHeader}>Relationship status:</h4>
                <select
                  value={formData.relationshipStatus}
                  onChange={(e) => handleChange("relationshipStatus", e.target.value)}
                  style={styles.select}
                >
                  <option value="single, never married">single, never married</option>
                  <option value="currently dating/or in committed relationship">currently dating/in committed relationship</option>
                  <option value="engaged">engaged</option>
                  <option value="married">married</option>
                  <option value="divorced">divorced</option>
                  <option value="separated">separated</option>
                </select>
              </div>
              
              <div style={styles.column}>
                <h4 style={styles.subSectionHeader}>Relationship Satisfaction:</h4>
                <select
                  value={formData.relationshipSatisfaction}
                  onChange={(e) => handleChange("relationshipSatisfaction", e.target.value)}
                  style={styles.select}
                  disabled={formData.relationshipStatus === "single, never married"}
                >
                  <option value="very satisfied">very satisfied</option>
                  <option value="satisfied">satisfied</option>
                  <option value="somewhat satisfied">somewhat satisfied</option>
                  <option value="dissatisfied">dissatisfied</option>
                  <option value="very dissatisfied">very dissatisfied</option>
                </select>
              </div>
            </div>
          </div>

          {/* Protective Factors/Support */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Protective Factors/Support System:</h3>
            <textarea
              rows="3"
              value={formData.protectiveFactors}
              onChange={(e) => handleChange("protectiveFactors", e.target.value)}
              style={styles.textArea}
              placeholder="Document client's protective factors and support systems..."
            />
          </div>

          {/* Strengths/Coping */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Strengths/Coping Skills:</h3>
            <textarea
              rows="3"
              value={formData.copingSkills}
              onChange={(e) => handleChange("copingSkills", e.target.value)}
              style={styles.textArea}
              placeholder="Document client's strengths and coping skills..."
            />
          </div>

          {/* Mental Status */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Mental Status Examination/Observations:</h3>
            <textarea
              rows="4"
              value={formData.mentalStatusExam}
              onChange={(e) => handleChange("mentalStatusExam", e.target.value)}
              style={styles.textArea}
              placeholder="Document observations about client's mental status and presentation..."
            />
          </div>
        </div>

        <div style={styles.modalButtons}>
          <button onClick={handleSave} style={styles.saveBtn}>
            Save Assessment
          </button>
          <button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * IntakeAssessmentViewModal - For viewing/editing existing assessment
 */
function IntakeAssessmentViewModal({ isOpen, onClose, note, onUpdate, onSendAsPDF }) {
  if (!isOpen || !note) return null;

  // Parse the note content
  let parsed = {};
  try {
    parsed = JSON.parse(note.notes);
  } catch (err) {
    console.error("Error parsing note", err);
  }

  const [editMode, setEditMode] = useState(false);
  const [date, setDate] = useState(parsed.date || "");
  const [formData, setFormData] = useState({
    // Basic form data
    presentingProblems: parsed.presentingProblems || "",
    familyMedicalHistory: parsed.familyMedicalHistory || "",
    
    // Trauma History
    traumaHistory: parsed.traumaHistory || "No trauma history reported",
    traumaHistoryOther: parsed.traumaHistoryOther || "",
    
    // Substance Abuse
    substanceAbuseFamilyHistory: parsed.substanceAbuseFamilyHistory || ["no family history reported"],
    substanceAbuseFamilyHistoryOther: parsed.substanceAbuseFamilyHistoryOther || "",
    substanceAbuseStatus: parsed.substanceAbuseStatus || "no history of substance abuse",
    
    // Previous Therapy 
    previousTherapyExperiences: parsed.previousTherapyExperiences || "No previous therapy experiences",
    previousTherapyNotes: parsed.previousTherapyNotes || "",
    
    // Self-harm history
    selfHarmHistory: parsed.selfHarmHistory || "",
    
    // Relationship
    relationshipStatus: parsed.relationshipStatus || "single, never married",
    relationshipSatisfaction: parsed.relationshipSatisfaction || "satisfied",
    
    // Support & Strengths
    protectiveFactors: parsed.protectiveFactors || "",
    copingSkills: parsed.copingSkills || "",
    
    // Mental Status
    mentalStatusExam: parsed.mentalStatusExam || ""
  });
  
  // Store client name from note for printing
  const [clientName, setClientName] = useState("");
  
  // Fetch client name on load
  useEffect(() => {
    if (note && note.client_id) {
      const fetchClientName = async () => {
        try {
          const res = await API.get(`/therapist/clients/${note.client_id}`);
          if (res.data && res.data.client_name) {
            setClientName(res.data.client_name);
          }
        } catch (err) {
          console.error("Error fetching client name:", err);
        }
      };
      
      fetchClientName();
    }
  }, [note]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Handle checkbox-type multiple selections
  const handleMultiSelectChange = (field, value) => {
    let updatedValues;
    
    if (formData[field].includes(value)) {
      // Remove value if it's already selected
      updatedValues = formData[field].filter(item => item !== value);
    } else {
      // Add value if it's not already selected
      updatedValues = [...formData[field], value];
      
      // If "no family history reported" is selected, remove it when other options are chosen
      if (value !== "no family history reported" && updatedValues.includes("no family history reported")) {
        updatedValues = updatedValues.filter(item => item !== "no family history reported");
      }
      
      // If "no family history reported" is selected, clear all other selections
      if (value === "no family history reported") {
        updatedValues = ["no family history reported"];
      }
    }
    
    setFormData({
      ...formData,
      [field]: updatedValues
    });
  };

  // Save the updated assessment
  const handleSave = () => {
    const updatedAssessment = {
      ...parsed,
      date,
      ...formData
    };
    
    onUpdate(note.id, updatedAssessment);
    setEditMode(false);
  };
  
  // Handle printing
  const handlePrint = () => {
    const noteData = {
      ...parsed,
      date,
      ...formData
    };
    
    printNote({notes: JSON.stringify(noteData)}, "IntakeAssessment", clientName);
  };

  // Helper to render form fields based on editMode
  const renderField = (field, label, fieldType = "text") => {
    if (editMode) {
      if (fieldType === "textarea") {
        return (
          <textarea
            rows="3"
            value={formData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            style={styles.textArea}
          />
        );
      } else if (fieldType === "text") {
        return (
          <input
            type="text"
            value={formData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            style={styles.input}
          />
        );
      }
    } else {
      return <div style={styles.readOnly}>{formData[field]}</div>;
    }
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>
          {editMode ? "Edit Intake Assessment" : "View Intake Assessment"}
        </h2>

        <div style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          {!editMode && (
            <>
              <button
                onClick={handlePrint}
                style={{ ...styles.button, backgroundColor: "var(--text-muted)" }}
              >
                Print/PDF
              </button>
              <button
                onClick={() => setEditMode(true)}
                style={{ ...styles.button, backgroundColor: "var(--accent-color)" }}
              >
                Edit
              </button>
            </>
          )}
          {onSendAsPDF && (
            <button onClick={() => onSendAsPDF(note)} style={{ ...styles.button, backgroundColor: "var(--primary-color)" }}>
              ✉️ Send as PDF
            </button>
          )}
          <button onClick={onClose} style={styles.cancelBtn}>
            Close
          </button>
        </div>

        <div style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "1rem" }}>
          Created: {new Date(note.created_at).toLocaleString()}
        </div>

        <div style={styles.formScrollArea}>
          {/* Date */}
          <div style={styles.formSection}>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Assessment Date:</label>
              {editMode ? (
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={styles.input}
                />
              ) : (
                <div style={styles.readOnly}>{date}</div>
              )}
            </div>
          </div>

          {/* Presenting Problems */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Presenting Problem(s), including current symptoms:</h3>
            {renderField("presentingProblems", "Presenting Problems", "textarea")}
          </div>

          {/* Family/Medical History */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>History: (family/medical)</h3>
            {renderField("familyMedicalHistory", "Family/Medical History", "textarea")}
          </div>

          {/* Trauma History */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Trauma History</h3>
            {editMode ? (
              <div style={styles.optionsContainer}>
                <select
                  value={formData.traumaHistory}
                  onChange={(e) => handleChange("traumaHistory", e.target.value)}
                  style={styles.select}
                >
                  <option value="No trauma history reported">No trauma history reported</option>
                  <option value="witnessed physical/verbal/sexual abuse towards others">Witnessed physical/verbal/sexual abuse towards others</option>
                  <option value="experienced physical/verbal/sexual abuse from others">Experienced physical/verbal/sexual abuse from others</option>
                  <option value="other">Other</option>
                </select>
                
                {formData.traumaHistory === "other" && (
                  <input
                    type="text"
                    value={formData.traumaHistoryOther}
                    onChange={(e) => handleChange("traumaHistoryOther", e.target.value)}
                    style={styles.input}
                    placeholder="Specify other trauma history..."
                  />
                )}
              </div>
            ) : (
              <div style={styles.readOnly}>
                {formData.traumaHistory === "other" 
                  ? `Other: ${formData.traumaHistoryOther}` 
                  : formData.traumaHistory}
              </div>
            )}
          </div>

          {/* Substance Abuse History */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Substance Abuse History</h3>
            <h4 style={styles.subSectionHeader}>Family History:</h4>
            
            {editMode ? (
              <div style={styles.checkboxContainer}>
                {[
                  { id: "noHistory", label: "no family history reported" },
                  { id: "father", label: "father" },
                  { id: "mother", label: "mother" },
                  { id: "grandparents", label: "grandparents" },
                  { id: "siblings", label: "siblings" },
                  { id: "spouse", label: "spouse/partner" },
                  { id: "other", label: "other" }
                ].map(option => (
                  <div key={option.id} style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id={`family-${option.id}`}
                      checked={formData.substanceAbuseFamilyHistory.includes(option.label)}
                      onChange={() => handleMultiSelectChange("substanceAbuseFamilyHistory", option.label)}
                    />
                    <label htmlFor={`family-${option.id}`}>{option.label}</label>
                  </div>
                ))}
                
                {formData.substanceAbuseFamilyHistory.includes("other") && (
                  <input
                    type="text"
                    value={formData.substanceAbuseFamilyHistoryOther}
                    onChange={(e) => handleChange("substanceAbuseFamilyHistoryOther", e.target.value)}
                    style={{...styles.input, marginTop: "0.5rem"}}
                    placeholder="Specify other family members..."
                  />
                )}
              </div>
            ) : (
              <div style={styles.readOnly}>
                {formData.substanceAbuseFamilyHistory.join(", ")}
                {formData.substanceAbuseFamilyHistory.includes("other") && 
                  ` (${formData.substanceAbuseFamilyHistoryOther})`}
              </div>
            )}

            <h4 style={styles.subSectionHeader}>Substance Abuse Status for Client:</h4>
            {editMode ? (
              <select
                value={formData.substanceAbuseStatus}
                onChange={(e) => handleChange("substanceAbuseStatus", e.target.value)}
                style={styles.select}
              >
                <option value="no history of substance abuse">no history of substance abuse</option>
                <option value="active abuse">active abuse</option>
                <option value="early full remission">early full remission</option>
                <option value="early partial remission">early partial remission</option>
                <option value="sustained full remission">sustained full remission</option>
                <option value="sustained partial remission">sustained partial remission</option>
              </select>
            ) : (
              <div style={styles.readOnly}>{formData.substanceAbuseStatus}</div>
            )}
          </div>

          {/* Previous Therapy Experiences */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Previous Therapy Experiences:</h3>
            {editMode ? (
              <>
                <select
                  value={formData.previousTherapyExperiences}
                  onChange={(e) => handleChange("previousTherapyExperiences", e.target.value)}
                  style={styles.select}
                >
                  <option value="No previous therapy experiences">No previous therapy experiences</option>
                  <option value="Prior outpatient psychotherapy">Prior outpatient psychotherapy</option>
                  <option value="prior inpatient psychotherapy">Prior inpatient psychotherapy</option>
                  <option value="multiple therapy experiences">Multiple therapy experiences</option>
                </select>
                
                <textarea
                  rows="3"
                  value={formData.previousTherapyNotes}
                  onChange={(e) => handleChange("previousTherapyNotes", e.target.value)}
                  style={{...styles.textArea, marginTop: "0.5rem"}}
                  placeholder="Notes about previous therapy experiences..."
                />
              </>
            ) : (
              <div>
                <div style={styles.readOnly}>{formData.previousTherapyExperiences}</div>
                {formData.previousTherapyNotes && (
                  <div style={{...styles.readOnly, marginTop: "0.5rem"}}>
                    <strong>Notes:</strong> {formData.previousTherapyNotes}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SI/HI/Self-harm */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>History/Current SI/HI/self-harm:</h3>
            {renderField("selfHarmHistory", "Self-harm History", "textarea")}
          </div>

          {/* Current relationship status */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Current relationship status/satisfaction:</h3>
            
            {editMode ? (
              <div style={styles.twoColumnSection}>
                <div style={styles.column}>
                  <h4 style={styles.subSectionHeader}>Relationship status:</h4>
                  <select
                    value={formData.relationshipStatus}
                    onChange={(e) => handleChange("relationshipStatus", e.target.value)}
                    style={styles.select}
                  >
                    <option value="single, never married">single, never married</option>
                    <option value="currently dating/or in committed relationship">currently dating/in committed relationship</option>
                    <option value="engaged">engaged</option>
                    <option value="married">married</option>
                    <option value="divorced">divorced</option>
                    <option value="separated">separated</option>
                  </select>
                </div>
                
                <div style={styles.column}>
                  <h4 style={styles.subSectionHeader}>Relationship Satisfaction:</h4>
                  <select
                    value={formData.relationshipSatisfaction}
                    onChange={(e) => handleChange("relationshipSatisfaction", e.target.value)}
                    style={styles.select}
                    disabled={formData.relationshipStatus === "single, never married"}
                  >
                    <option value="very satisfied">very satisfied</option>
                    <option value="satisfied">satisfied</option>
                    <option value="somewhat satisfied">somewhat satisfied</option>
                    <option value="dissatisfied">dissatisfied</option>
                    <option value="very dissatisfied">very dissatisfied</option>
                  </select>
                </div>
              </div>
            ) : (
              <div>
                <div style={styles.readOnly}>
                  <strong>Status:</strong> {formData.relationshipStatus}
                </div>
                {formData.relationshipStatus !== "single, never married" && (
                  <div style={{...styles.readOnly, marginTop: "0.5rem"}}>
                    <strong>Satisfaction:</strong> {formData.relationshipSatisfaction}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Protective Factors/Support */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Protective Factors/Support System:</h3>
            {renderField("protectiveFactors", "Protective Factors", "textarea")}
          </div>

          {/* Strengths/Coping */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Strengths/Coping Skills:</h3>
            {renderField("copingSkills", "Coping Skills", "textarea")}
          </div>

          {/* Mental Status */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Mental Status Examination/Observations:</h3>
            {renderField("mentalStatusExam", "Mental Status Exam", "textarea")}
          </div>
        </div>

        {editMode && (
          <div style={styles.modalButtons}>
            <button onClick={handleSave} style={styles.saveBtn}>
              Save Changes
            </button>
            <button 
              onClick={() => setEditMode(false)}
              style={styles.cancelBtn}
            >
              Cancel Edits
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles imported from the main component or styles.js
const styles = {
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "1rem"
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "6px",
    maxHeight: "90vh",
    overflowY: "auto",
    width: "700px",
    maxWidth: "94vw",
    boxSizing: "border-box",
    position: "relative"
  },
  modalHeader: {
    fontSize: "1.25rem",
    margin: 0,
    marginBottom: "0.5rem"
  },
  clientInfo: {
    marginBottom: "1rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "1rem"
  },
  formScrollArea: {
    maxHeight: "60vh",
    overflowY: "auto",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px"
  },
  formSection: {
    marginBottom: "1.5rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    border: "1px solid var(--border-color)"
  },
  sectionHeader: {
    fontSize: "1rem",
    fontWeight: "bold",
    margin: "0 0 0.75rem 0",
    color: "var(--text-strong)",
    borderBottom: "1px solid var(--border-color)",
    paddingBottom: "0.25rem"
  },
  subSectionHeader: {
    fontSize: "0.9rem",
    fontWeight: "bold",
    margin: "0.75rem 0 0.5rem 0",
    color: "var(--text-strong)"
  },
  textArea: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    resize: "vertical",
    fontFamily: "inherit",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  select: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    backgroundColor: "#fff",
    fontSize: "0.9rem"
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  smallButton: {
    padding: "0.25rem 0.5rem",
    fontSize: "0.8rem",
    backgroundColor: "var(--text-muted)",
    color: "#fff",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dateInput: {
    padding: "0.25rem",
    border: "1px solid var(--border-color)",
    borderRadius: "3px",
    marginLeft: "0.5rem"
  },
  checkboxContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem"
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    minWidth: "120px"
  },
  twoColumnSection: {
    display: "flex",
    gap: "1rem"
  },
  column: {
    flex: 1
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  },
  readOnly: {
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    minHeight: "1rem"
  },
  formField: {
    marginBottom: "0.75rem"
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: "0.25rem",
    display: "block"
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    marginTop: "1rem"
  },
  saveBtn: {
    backgroundColor: "var(--success-color)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  cancelBtn: {
    backgroundColor: "var(--text-strong)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  button: {
    backgroundColor: "var(--primary-color)",
    border: "none",
    color: "#fff",
    padding: "0.4rem 0.75rem",
    borderRadius: "4px",
    cursor: "pointer"
  }
};

export { IntakeAssessmentModal, IntakeAssessmentViewModal };